// Error tracking and reporting system

interface ErrorReport {
  message: string;
  stack?: string;
  url: string;
  lineNumber?: number;
  columnNumber?: number;
  userAgent: string;
  timestamp: string;
  userId?: string;
  route: string;
  additionalContext?: Record<string, any>;
}

interface PerformanceReport {
  metric: string;
  value: number;
  timestamp: string;
  route: string;
  userId?: string;
  additionalData?: Record<string, any>;
}

class ErrorTracker {
  private apiEndpoint: string;
  private userId?: string;
  private enabled: boolean;

  constructor(apiEndpoint = '/api/errors', enabled = true) {
    this.apiEndpoint = apiEndpoint;
    this.enabled = enabled;
    this.setupGlobalErrorHandlers();
  }

  setUserId(userId: string) {
    this.userId = userId;
  }

  private setupGlobalErrorHandlers() {
    if (!this.enabled || typeof window === 'undefined') return;

    // Handle uncaught JavaScript errors
    window.addEventListener('error', (event) => {
      this.reportError({
        message: event.message,
        stack: event.error?.stack,
        url: event.filename || window.location.href,
        lineNumber: event.lineno,
        columnNumber: event.colno,
        userAgent: navigator.userAgent,
        timestamp: new Date().toISOString(),
        userId: this.userId,
        route: window.location.pathname,
      });
    });

    // Handle unhandled promise rejections
    window.addEventListener('unhandledrejection', (event) => {
      let message = 'Unhandled Promise Rejection';
      let stack = '';

      if (event.reason instanceof Error) {
        message = event.reason.message;
        stack = event.reason.stack || '';
      } else if (typeof event.reason === 'string') {
        message = event.reason;
      } else {
        message = JSON.stringify(event.reason);
      }

      this.reportError({
        message,
        stack,
        url: window.location.href,
        userAgent: navigator.userAgent,
        timestamp: new Date().toISOString(),
        userId: this.userId,
        route: window.location.pathname,
        additionalContext: {
          type: 'unhandledrejection',
          reason: event.reason
        }
      });
    });

    // Handle resource loading errors
    window.addEventListener('error', (event) => {
      if (event.target !== window) {
        const target = event.target as HTMLElement;
        const tagName = target.tagName;
        const src = (target as any).src || (target as any).href;

        if (src) {
          this.reportError({
            message: `Resource loading failed: ${tagName}`,
            url: src,
            userAgent: navigator.userAgent,
            timestamp: new Date().toISOString(),
            userId: this.userId,
            route: window.location.pathname,
            additionalContext: {
              type: 'resource-error',
              tagName,
              resourceUrl: src
            }
          });
        }
      }
    }, true);
  }

  reportError(error: Partial<ErrorReport>) {
    if (!this.enabled) {
      console.error('Error tracked:', error);
      return;
    }

    const errorReport: ErrorReport = {
      message: error.message || 'Unknown error',
      stack: error.stack,
      url: error.url || window.location.href,
      lineNumber: error.lineNumber,
      columnNumber: error.columnNumber,
      userAgent: navigator.userAgent,
      timestamp: new Date().toISOString(),
      userId: this.userId,
      route: window.location.pathname,
      ...error
    };

    // Send to backend
    this.sendErrorReport(errorReport).catch(() => {
      // Fallback: store in localStorage for later retry
      this.storeErrorForRetry(errorReport);
    });
  }

  reportPerformanceMetric(metric: string, value: number, additionalData?: Record<string, any>) {
    if (!this.enabled) {
      // console.log(`Performance metric: ${metric} = ${value}`, additionalData);
      return;
    }

    const report: PerformanceReport = {
      metric,
      value,
      timestamp: new Date().toISOString(),
      route: window.location.pathname,
      userId: this.userId,
      additionalData
    };

    this.sendPerformanceReport(report).catch(() => {
      // Silently fail for performance metrics
    });
  }

  private async sendErrorReport(error: ErrorReport) {
    try {
      await fetch(this.apiEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(error),
      });
    } catch (fetchError) {
      console.error('Failed to send error report:', fetchError);
      throw fetchError;
    }
  }

  private async sendPerformanceReport(report: PerformanceReport) {
    try {
      await fetch(`${this.apiEndpoint}/performance`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(report),
      });
    } catch (fetchError) {
      console.error('Failed to send performance report:', fetchError);
      throw fetchError;
    }
  }

  private storeErrorForRetry(error: ErrorReport) {
    try {
      const stored = localStorage.getItem('error-reports') || '[]';
      const errors = JSON.parse(stored);
      errors.push(error);

      // Keep only last 10 errors
      if (errors.length > 10) {
        errors.splice(0, errors.length - 10);
      }

      localStorage.setItem('error-reports', JSON.stringify(errors));
    } catch (storageError) {
      console.error('Failed to store error for retry:', storageError);
    }
  }

  async retryStoredErrors() {
    if (!this.enabled) return;

    try {
      const stored = localStorage.getItem('error-reports');
      if (!stored) return;

      const errors = JSON.parse(stored);
      const successes: boolean[] = [];

      for (const error of errors) {
        try {
          await this.sendErrorReport(error);
          successes.push(true);
        } catch {
          successes.push(false);
        }
      }

      // Remove successfully sent errors
      const remainingErrors = errors.filter((_: any, index: number) => !successes[index]);
      localStorage.setItem('error-reports', JSON.stringify(remainingErrors));
    } catch (retryError) {
      console.error('Failed to retry stored errors:', retryError);
    }
  }

  // Track React component errors
  captureComponentError(error: Error, errorInfo: React.ErrorInfo, componentName?: string) {
    this.reportError({
      message: error.message,
      stack: error.stack,
      additionalContext: {
        type: 'react-error',
        componentName,
        componentStack: errorInfo.componentStack
      }
    });
  }

  // Track API errors
  captureAPIError(endpoint: string, error: any, requestData?: any) {
    this.reportError({
      message: `API Error: ${endpoint}`,
      stack: error?.stack,
      additionalContext: {
        type: 'api-error',
        endpoint,
        error: error?.message || error,
        requestData
      }
    });
  }

  // Track chunk loading errors specifically
  captureChunkError(chunkName: string, error: any) {
    this.reportError({
      message: `Chunk loading failed: ${chunkName}`,
      stack: error?.stack,
      additionalContext: {
        type: 'chunk-error',
        chunkName,
        error: error?.message || error
      }
    });
  }
}

// Create singleton instance
export const errorTracker = new ErrorTracker();

// Convenience functions
export const reportError = (error: Partial<ErrorReport>) => errorTracker.reportError(error);
export const reportPerformanceMetric = (metric: string, value: number, additionalData?: Record<string, any>) =>
  errorTracker.reportPerformanceMetric(metric, value, additionalData);
export const setUserId = (userId: string) => errorTracker.setUserId(userId);

// React Error Boundary helper
export const captureComponentError = (error: Error, errorInfo: React.ErrorInfo, componentName?: string) =>
  errorTracker.captureComponentError(error, errorInfo, componentName);

// Performance monitoring helpers
export const measurePerformance = async <T>(
  name: string,
  fn: () => Promise<T> | T,
  additionalData?: Record<string, any>
): Promise<T> => {
  const startTime = performance.now();

  try {
    const result = await fn();
    const duration = performance.now() - startTime;
    reportPerformanceMetric(name, duration, additionalData);
    return result;
  } catch (error) {
    const duration = performance.now() - startTime;
    reportPerformanceMetric(`${name}_error`, duration, { error: error instanceof Error ? error.message : error, ...additionalData });
    throw error;
  }
};

export default errorTracker;